import { useCallback, useEffect, useRef, useState } from 'react';
import { fmtDateTime } from '../../../../utils/ruDates';

const RECHECK = [['WEEKLY', 'Раз в неделю'], ['ONCE', 'Один раз']];
const STATUS = {
  RUNNING: 'Идёт', RETURNING: 'Возвращаем цену', DONE: 'Завершён', FAILED: 'Ошибка',
  QUEUED: 'В очереди — пойдёт после текущего поиска кабинета', CANCELLED: 'Отменён',
};
const PHASE = {
  BASE: 'замер исходной цены', SEND: 'ставим пробную цену', SENDING: 'ставим пробную цену',
  WAIT: 'ждём, пока OZON пересчитает витрину (2 мин)', RECHECK: 'витрина не обновилась — перезамер',
  MEASURE: 'замер витрины', RETURN: 'возвращаем цену', FINAL: 'контрольный замер', DONE: '',
};
const RESULT = {
  EDGE: 'Граница скидки найдена', PARTIAL: 'Граница сужена — уточним следующим поиском',
  NO_EDGE: 'Один уровень скидки на проверенном отрезке', NO_BUDGET: 'Нет свободных смен цены',
  NO_DATA: 'Витрина не замерена', NO_RANGE: 'Снижать некуда', REFUSED: 'OZON не принял пробную цену',
  LIMIT: 'OZON ограничил смены цены — продолжим позже',
  FOREIGN: 'Цену поменяли со стороны — поиск остановлен без возврата',
};
const OPEN = ['RUNNING', 'RETURNING'];
const LEGEND = [
  ['Соинвест', 'сколько процентов цены продавца доплачивает OZON: витрина = цена продавца − соинвест.'],
  ['Уровень скидки OZON', 'диапазон цен, внутри которого соинвест почти не меняется.'],
  ['Граница скидки', 'цена, где +1 ₽ к цене продавца меняет витрину в полтора раза: выше — скидка меньше.'],
  ['Поиск границы скидки', 'несколько пробных цен вниз с замером витрины после каждой; в конце цена возвращается.'],
  ['Допустимые цены', 'до какой цены поиск может опускаться и ниже какой цены стоять нельзя по рентабельности.'],
];

const rub = value => value == null ? '—' : `${Math.round(Number(value)).toLocaleString('ru-RU')} ₽`;
// Соинвест — доля цены продавца, которую доплачивает OZON: (цена − витрина) / цена.
// Хранится отношение «витрина / цена», показывается соинвест в процентах с сотыми.
const coinvest = ratio => ratio == null ? '—'
  : `${((1 - Number(ratio)) * 100).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;
const failure = err => typeof err?.detail === 'string' ? err.detail : err?.message || 'Не удалось выполнить запрос';
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const settingsOf = s => s && ({ enabled: !!s.enabled, ads_drop_pct: Number(s.ads_drop_pct ?? 0), recheck: s.recheck || 'WEEKLY' });

function Sub({ children, muted = true, warn = false }) {
  return <small className={warn ? 'oz-auto__unavailable' : muted ? 'oz-auto__muted' : ''} style={{ display: 'block' }}>{children}</small>;
}

function Points({ base, request, sku, live }) {
  const [points, setPoints] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    request(`${base}/coinvest/points?sku=${sku}&days=14`)
      .then(data => setPoints(data.points))
      .catch(err => setError(failure(err)));
  }, [base, request, sku, live]);
  if (error) return <p role='alert' className='bidder__error'>{error}</p>;
  if (!points) return <p>Загрузка…</p>;
  if (!points.length) return <p className='oz-auto__muted'>Замеров пока нет.</p>;
  return <div className='oz-auto__table-scroll'><table>
    <thead><tr><th>Когда</th><th>Цена продавца</th><th>Витрина</th><th>Соинвест</th><th>Регион</th></tr></thead>
    <tbody>{points.slice().reverse().map((p, index) => <tr key={`${p.at}-${index}`}>
      <td>{fmtDateTime(p.at)}</td><td>{rub(p.seller)}</td><td>{rub(p.shelf)}</td>
      <td>{coinvest(p.ratio)}</td><td>{p.region || '—'}</td>
    </tr>)}</tbody>
  </table></div>;
}

function Allowed({ live }) {
  if (!live) return <span className='oz-auto__muted'>—</span>;
  if (live.limit_why) return <Sub warn>{live.limit_why[0].toUpperCase() + live.limit_why.slice(1)}</Sub>;
  return <span>
    {live.search_floor != null && <>поиск вниз до {rub(live.search_floor)}</>}
    {live.stand_floor != null && <Sub>стоять не ниже {rub(live.stand_floor)}
      {live.min_margin_pct != null ? ` (мин. рентабельность ${live.min_margin_pct}%)` : ''}</Sub>}
    {live.breakeven != null && <Sub>безубыточность без рекламы {rub(live.breakeven)}</Sub>}
    {live.ads_note && <Sub>{live.ads_note}</Sub>}
  </span>;
}

function Progress({ sprint, names, busy, onStop, limits }) {
  const items = Object.values(sprint.items);
  const used = items.reduce((sum, item) => sum + (item.used || 0), 0);
  const budget = items.reduce((sum, item) => sum + (item.budget || 0), 0) || 1;
  const left = items.filter(item => !item.done).length;
  // Шаг — 2 минуты ожидания витрины плюс замер карточек (~45 с на товар).
  const stepMinutes = ((limits?.settle_seconds || 120) + (limits?.per_sku_seconds || 45) * Math.max(1, left)) / 60;
  return <div className='oz-auto__planner'>
    <div className='oz-auto__panel-head'>
      <strong>Идёт поиск границы скидки: {STATUS[sprint.status]}, {PHASE[sprint.phase] || sprint.phase}</strong>
      <button type='button' disabled={busy} onClick={() => onStop(sprint.id)}>Остановить и вернуть цену</button>
    </div>
    <div className='oz-auto__progress' aria-label='Ход поиска'><i style={{ width: `${Math.min(100, used / budget * 100)}%` }} /></div>
    <small>Шаг {sprint.step}; проб {used} из {budget}; ищем ещё по {left} арт.
      {sprint.status === 'RUNNING' && left > 0 && `, один шаг ~${Math.ceil(stepMinutes)} мин`}</small>
    {Object.entries(sprint.items).filter(([, item]) => !item.done && item.probe != null).map(([sku, item]) =>
      <Sub key={sku} muted={false}>{names[sku] || item.offer_id}: проба {rub(item.probe)} (исходная {rub(item.start)})</Sub>)}
  </div>;
}

function SprintRow({ sprint, names, busy, onStop }) {
  const open = OPEN.includes(sprint.status);
  return <tr>
    <td>{fmtDateTime(sprint.started_at)}{sprint.finished_at && <Sub>до {fmtDateTime(sprint.finished_at)}</Sub>}</td>
    <td>{STATUS[sprint.status] || sprint.status}{open && PHASE[sprint.phase] && <Sub>{PHASE[sprint.phase]}, шаг {sprint.step}</Sub>}</td>
    <td>{Object.entries(sprint.items).map(([sku, item]) => <div key={sku} style={{ marginBottom: 4 }}>
      <strong>{names[sku] || `SKU ${sku}`}</strong> <small className='oz-auto__muted'>
        исходная {rub(item.start)}, поиск до {rub(item.floor)}, проб {item.used ?? 0} из {item.budget ?? '—'}</small>
      {item.result && <Sub muted={false}>{RESULT[item.result.kind] || item.result.kind}
        {item.result.lo != null && item.result.hi != null && `: ${rub(item.result.lo)} – ${rub(item.result.hi)}`}</Sub>}
      {!item.done && item.lo != null && <Sub>отрезок {rub(item.lo)} – {rub(item.hi)}, проба {rub(item.probe)}</Sub>}
      {item.note && <Sub>{item.note}</Sub>}
      {(item.plan_notes || []).map(note => <Sub key={note}>{note}</Sub>)}
    </div>)}</td>
    <td>{sprint.note || '—'}</td>
    <td>{open && <button type='button' disabled={busy} onClick={() => onStop(sprint.id)}>Остановить и вернуть цену</button>}
      {sprint.status === 'QUEUED' && <button type='button' disabled={busy} onClick={() => onStop(sprint.id)}>Отменить</button>}</td>
  </tr>;
}

function Confirm({ rows, limits, onCancel, onGo, busy }) {
  const go = rows.filter(row => !row.live?.limit_why);
  const skip = rows.filter(row => row.live?.limit_why);
  const steps = 9;
  const minutes = Math.ceil(steps * ((limits?.settle_seconds || 120) + (limits?.per_sku_seconds || 45) * Math.max(1, go.length)) / 60);
  return <div className='oz-auto__notice' role='dialog' aria-label='Подтверждение поиска границы скидки'>
    <strong>Запустить поиск границы скидки?</strong>
    <ul>
      <li>Цена каждого товара несколько раз опустится (не больше {limits?.max_drop_pct ?? 20}% от текущей и не ниже
        безубыточности), после каждой пробы через 2 минуты замеряется витрина.</li>
      <li>До 8 смен цены на товар; в конце цена, «ограничение для акций» и цена до скидки возвращаются как были.</li>
      <li>Займёт примерно до {minutes} мин; локальный агент на это время занят поиском.</li>
      {go.length > MAX_SHOWN_SKUS(limits) && <li>Не больше {limits.max_skus} товаров за раз — остальные пойдут следующим поиском.</li>}
    </ul>
    {skip.length > 0 && <>
      <Sub muted={false}>Пропустим {skip.length}:</Sub>
      {skip.map(row => <Sub key={row.sku}>{row.name || row.offer_id}: {row.live.limit_why}</Sub>)}
    </>}
    <div className='oz-auto__panel-actions' style={{ marginTop: 8 }}>
      <button type='button' className='oz-auto__primary' disabled={busy || !go.length} onClick={onGo}>
        Запустить по {go.length} арт.</button>
      <button type='button' disabled={busy} onClick={onCancel}>Отмена</button>
    </div>
  </div>;
}

const MAX_SHOWN_SKUS = limits => limits?.max_skus || 20;

export default function OzonAutomationCoinvest({ base, request, busy: outerBusy, mode }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(null);
  const [selected, setSelected] = useState([]);
  const [opened, setOpened] = useState(null);
  const [confirming, setConfirming] = useState(false);
  // Ответ, пришедший после более свежего запроса, не должен затереть новые данные.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    try {
      const next = await request(`${base}/coinvest`);
      if (id !== requestId.current) return;
      setData(next);
      setForm(current => current || settingsOf(next.settings));
      setError('');
    } catch (err) { if (id === requestId.current) setError(failure(err)); }
  }, [base, request]);

  useEffect(() => { load(); }, [load]);

  // Пока идёт поиск, цена меняется каждые несколько минут — ход виден без кнопки «Обновить».
  const running = (data?.sprints || []).some(sprint => OPEN.includes(sprint.status) || sprint.status === 'QUEUED');
  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(timer);
  }, [running, load]);

  const call = async (path, options) => {
    setBusy(true); setError('');
    try { await request(path, options); await load(); return true; }
    catch (err) { setError(failure(err)); return false; }
    finally { setBusy(false); }
  };

  const dirty = data && form && !same(form, settingsOf(data.settings));
  const save = async () => {
    if (await call(`${base}/coinvest`, { method: 'PUT', body: JSON.stringify(form) })) setForm(null);
  };
  const start = async () => {
    if (await call(`${base}/coinvest/sprints`, { method: 'POST', body: JSON.stringify({ sku_ids: selected }) })) {
      setSelected([]);
      setConfirming(false);
    }
  };
  const stop = id => call(`${base}/coinvest/sprints/${id}`, { method: 'DELETE' });
  const toggle = sku => setSelected(list => list.includes(sku) ? list.filter(s => s !== sku) : [...list, sku]);

  if (!data) return <div className='oz-auto__panel'>{error
    ? <p role='alert' className='bidder__error'>{error}</p> : <p>Загрузка…</p>}</div>;

  const names = Object.fromEntries(data.articles.map(row => [String(row.sku), row.name || row.offer_id]));
  const disabled = busy || outerBusy;
  const runningSkus = new Set(data.sprints.filter(s => OPEN.includes(s.status) || s.status === 'QUEUED')
    .flatMap(s => Object.keys(s.items)));
  // «Выбрать все» берёт только то, что можно запустить: без чужого ценового
  // инструмента и не в идущем или ждущем поиске.
  const isBlocked = row => !!(row.owner && row.owner !== 'Контроль соинвеста') || runningSkus.has(String(row.sku));
  const selectable = data.articles.filter(row => !isBlocked(row)).map(row => row.sku);
  // «Наблюдение» в кабинет ничего не отправляет — и поиск тоже: он меняет цену.
  const observing = (mode || data.mode) === 'OBSERVE';
  const blockedWhy = observing ? 'В режиме «Наблюдение» цену не меняем — переключите на «С подтверждением» или «Автоматический».'
    : !data.agent.online ? 'Локальный агент не на связи — без замера витрины поиск не запускается.'
      : !selected.length ? 'Отметьте товары в таблице.' : '';
  const live = data.sprints.filter(s => OPEN.includes(s.status));
  const queued = data.sprints.filter(s => s.status === 'QUEUED');
  const history = data.sprints.filter(s => !OPEN.includes(s.status) && s.status !== 'QUEUED');
  const selectedRows = data.articles.filter(row => selected.includes(row.sku));

  return <>
    {data.warnings.length > 0 && <div className='oz-auto__notice'>
      <strong>Требует внимания</strong><ul>{data.warnings.map(text => <li key={text}>{text}</li>)}</ul></div>}
    {error && <p role='alert' className='bidder__error'>{error}</p>}

    {live.map(sprint => <Progress key={sprint.id} sprint={sprint} names={names} busy={disabled}
      onStop={stop} limits={data.limits_live} />)}
    {queued.length > 0 && <div className='oz-auto__notice'>
      В очереди {queued.length} поиск(а) — пойдут после текущего поиска кабинета.
      {queued.map(s => <button key={s.id} type='button' disabled={disabled} onClick={() => stop(s.id)}>
        Отменить ({Object.keys(s.items).length} арт.)</button>)}</div>}

    <section className='oz-auto__panel'>
      <div className='oz-auto__panel-head'>
        <h4>Товары</h4>
        <div className='oz-auto__panel-actions'>
          {blockedWhy && <small className='oz-auto__muted'>{blockedWhy}</small>}
          <button type='button' className='oz-auto__primary' disabled={disabled || !!blockedWhy}
            onClick={() => setConfirming(true)}>Найти границу скидки ({selected.length})</button>
        </div>
      </div>
      <div className='oz-auto__legend'>
        {LEGEND.map(([term, text]) => <span key={term}><b>{term}</b> — {text}</span>)}
      </div>
      {confirming && <Confirm rows={selectedRows} limits={data.limits_live} busy={disabled}
        onCancel={() => setConfirming(false)} onGo={start} />}
      <div className='oz-auto__table-scroll'><table>
        <thead><tr><th><input type='checkbox' aria-label='Выбрать все доступные товары'
          disabled={!selectable.length}
          checked={selectable.length > 0 && selectable.every(sku => selected.includes(sku))}
          onChange={e => setSelected(e.target.checked ? selectable : [])} /></th>
          <th>Товар</th><th>Сейчас</th><th>Допустимые цены</th><th>Вывод</th><th>Смен цены</th><th /></tr></thead>
        <tbody>{data.articles.map(row => {
          const blocked = isBlocked(row);
          return [<tr key={row.sku}>
            <td><input type='checkbox' disabled={blocked} checked={selected.includes(row.sku)}
              onChange={() => toggle(row.sku)} aria-label={`Выбрать SKU ${row.sku}`} /></td>
            <td>{row.name || row.offer_id}<Sub>SKU {row.sku}{row.offer_id ? `, ${row.offer_id}` : ''}</Sub>
              {row.owner && row.owner !== 'Контроль соинвеста' && <Sub warn>
                ценой ведёт «{row.owner}» — поиск границы скидки с ним несовместим</Sub>}
              {row.corridor && <Sub>РРЦ, диапазон: витрина {rub(row.corridor.mrc)} – {rub(row.corridor.rrc)}</Sub>}
              {row.live?.fbo_stock > 0 && <Sub warn>есть остаток на FBO ({row.live.fbo_stock} шт.): с ним соинвест ниже,
                чем при одном FBS</Sub>}</td>
            <td>{row.last_point ? <>{rub(row.last_point.seller)} → витрина {rub(row.last_point.shelf)}
              <Sub>соинвест {coinvest(row.last_point.ratio)}, {fmtDateTime(row.last_point.at)}</Sub></>
              : row.live?.price ? <>{rub(row.live.price)}<Sub>замеров поиска ещё не было</Sub></> : '—'}</td>
            <td><Allowed live={row.live} /></td>
            <td>{row.result && <strong style={{ display: 'block' }}>{RESULT[row.result.kind] || row.result.kind}</strong>}
              <span>{row.verdict}</span>
              {row.corridor && row.deep_shelf != null && row.deep_shelf < row.corridor.mrc && <Sub warn>
                витрина там ≈ {rub(row.deep_shelf)} — ниже МРЦ {rub(row.corridor.mrc)}, этот уровень недоступен</Sub>}
              {row.last_sprint && <Sub>поиск от {fmtDateTime(row.last_sprint.at)}</Sub>}</td>
            <td>{row.changes_last_hour} из 10 за час</td>
            <td><button type='button' aria-expanded={opened === row.sku}
              onClick={() => setOpened(opened === row.sku ? null : row.sku)}>
              {opened === row.sku ? 'Скрыть замеры' : 'Замеры'}</button></td>
          </tr>,
          opened === row.sku && <tr key={`${row.sku}-points`}><td colSpan={7}>
            <Points base={base} request={request} sku={row.sku} live={row.last_point?.at} /></td></tr>];
        })}</tbody>
      </table></div>
    </section>

    <section className='oz-auto__panel'>
      <div className='oz-auto__panel-head'>
        <h4>Настройки</h4>
        <div className='oz-auto__panel-actions'>
          {dirty && <small className='oz-auto__unavailable'>Изменения не сохранены</small>}
          <button type='button' disabled={disabled || !dirty} onClick={save}>Сохранить</button>
        </div>
      </div>
      <details>
        <summary>Как это работает</summary>
        <p>Витрина идёт за ценой продавца уровнями: внутри уровня соинвест почти постоянен, а на границе один рубль
          меняет витрину в полтора раза. Поиск идёт только вниз: глубже скидку OZON даёт только при снижении цены.
          Он замеряет витрину на исходной цене, затем на нижней границе поиска и делит пополам отрезок, где витрина прыгнула.</p>
        {data.notes.map(text => <p key={text}>{text}</p>)}
        <p>Пока поиск только измеряет — ставить цену на найденный уровень система начнёт на следующем этапе.</p>
      </details>
      <div className='oz-auto__settings'>
        <label className='is-inline'>
          <input type='checkbox' checked={!!form?.enabled}
            onChange={e => setForm({ ...form, enabled: e.target.checked })} />
          <span>Контроль соинвеста включён
            <Sub>Постоянное наблюдение и автоматические поиски появятся на следующем этапе.</Sub></span></label>
        <label>Снижение рекламы на заказ после перехода, %
          <input type='number' min='0' max='90' step='any' value={form?.ads_drop_pct ?? 0}
            onChange={e => setForm({ ...form, ads_drop_pct: e.target.value === '' ? 0 : Number(e.target.value) })} />
          <small className='oz-auto__muted'>Сколько рекламы на заказ закладывать в цену, ниже которой стоять нельзя,
            на более глубоком уровне скидки. 0 — реклама останется как сейчас (консервативно). 50 — вдвое: ДРР 20%
            станет 10%. После сохранения допустимые цены в таблице пересчитаются сразу.</small></label>
        <label>Повторно проверять товар, где граница не найдена
          <select value={form?.recheck || 'WEEKLY'} onChange={e => setForm({ ...form, recheck: e.target.value })}>
            {RECHECK.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select></label>
      </div>
    </section>

    <section className='oz-auto__panel'>
      <h4>История поисков</h4>
      {!history.length ? <p className='oz-auto__muted'>Поисков ещё не было.</p> :
        <details>
          <summary>Показать {history.length}</summary>
          <div className='oz-auto__table-scroll'><table>
            <thead><tr><th>Когда</th><th>Статус</th><th>Товары</th><th>Заметки</th><th /></tr></thead>
            <tbody>{history.map(sprint => <SprintRow key={sprint.id} sprint={sprint} names={names}
              busy={disabled} onStop={stop} />)}</tbody>
          </table></div>
        </details>}
    </section>
  </>;
}
